import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import UserNavbar from '../components/UserNavbar';
import { toast } from 'react-toastify';

function UserSettings() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [userId, setUserId] = useState(null);

  // Settings state
  const [settings, setSettings] = useState({
    // Security
    twoFactorEnabled: false,
    // Notification Access
    enableNotifications: false,
    // Location Access
    allowLocation: false,
    whileUsingApp: false,
    // Gallery Access
    allowGalleryAccess: false,
    askEveryTime: false,
  });

  useEffect(() => {
    checkSession();
  }, []);

  const checkSession = async () => {
    try {
      const res = await api.get('user/session-check/');
      if (!res.data.authenticated) {
        navigate('/user/login');
        return;
      }
      const uid = res.data.user_id;
      setUserId(uid);
      // Load settings from backend
      await loadSettings(uid);
    } catch (err) {
      console.error("Session check failed:", err);
      navigate('/user/login');
    } finally {
      setLoading(false);
    }
  };

  const loadSettings = async (uid) => {
    try {
      // Load notification settings from backend
      const notificationRes = await api.get('notification/user/settings/');
      if (notificationRes.data.enable_notifications !== undefined) {
        setSettings(prev => ({
          ...prev,
          enableNotifications: notificationRes.data.enable_notifications
        }));
      }

      try {
        const twoFactorRes = await api.get('user/two-factor/');
        if (twoFactorRes.data.two_factor_enabled !== undefined) {
          setSettings(prev => ({
            ...prev,
            twoFactorEnabled: !!twoFactorRes.data.two_factor_enabled,
          }));
        }
      } catch (twoFactorErr) {
        console.error('Failed to load 2FA settings:', twoFactorErr);
      }
      
      // Load other settings from localStorage (location, gallery, etc.)
      const savedSettings = localStorage.getItem(`userSettings_${uid}`);
      if (savedSettings) {
        const parsed = JSON.parse(savedSettings);
        setSettings(prev => ({
          ...prev,
          allowLocation: parsed.allowLocation || false,
          whileUsingApp: parsed.whileUsingApp || false,
          allowGalleryAccess: parsed.allowGalleryAccess || false,
          askEveryTime: parsed.askEveryTime || false,
        }));
      }
    } catch (err) {
      console.error("Failed to load settings:", err);
      // Default to enabled if API fails
      setSettings(prev => ({
        ...prev,
        enableNotifications: true
      }));
    }
  };

  const handleToggle = async (key) => {
    const newSettings = {
      ...settings,
      [key]: !settings[key]
    };
    setSettings(newSettings);
    
    // If it's the notification toggle, update backend
    if (key === 'enableNotifications') {
      try {
        await api.post('notification/user/settings/update/', {
          enable_notifications: newSettings.enableNotifications
        });
        toast.success(`Notifications ${newSettings.enableNotifications ? 'enabled' : 'disabled'}`);
      } catch (err) {
        console.error("Failed to update notification settings:", err);
        toast.error("Failed to update notification settings");
        // Revert on error
        setSettings(settings);
      }
    } else if (key === 'twoFactorEnabled') {
      try {
        await api.post('user/two-factor/', {
          two_factor_enabled: newSettings.twoFactorEnabled,
        });
        toast.success(
          `Two-factor authentication ${newSettings.twoFactorEnabled ? 'enabled' : 'disabled'}`
        );
      } catch (err) {
        console.error('Failed to update 2FA settings:', err);
        toast.error(err.response?.data?.error || 'Failed to update 2FA settings');
        setSettings(settings);
      }
    } else {
      // Save other settings to localStorage
      localStorage.setItem(`userSettings_${userId}`, JSON.stringify(newSettings));
    }
  };

  if (loading) {
    return (
      <>
        <UserNavbar />
        <div className="min-vh-100 d-flex align-items-center justify-content-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <UserNavbar />
      <div className="container py-4 py-md-5">
        <div className="row justify-content-center">
          <div className="col-lg-8 col-xl-6">
            {/* Header */}
            <div className="d-flex align-items-center mb-4">
              <button 
                className="btn btn-link text-decoration-none text-muted p-0 me-3" 
                onClick={() => navigate(-1)}
              >
                <i className="bi bi-arrow-left fs-4"></i>
              </button>
              <h2 className="fw-bold mb-0">Settings</h2>
            </div>

            {/* Settings Card */}
            <div className="card border-0 shadow-sm rounded-4">
              <div className="card-body p-0">
                {/* Tab Content */}
                <div className="p-4">
                  <div>
                      {/* Payments & Billing */}
                      <div className="mb-4">
                        <h6 className="fw-bold text-dark mb-3">Payments & Billing</h6>

                        <button
                          type="button"
                          className="w-100 d-flex align-items-center justify-content-between py-3 border-bottom border-0 bg-transparent text-start"
                          onClick={() => navigate('/user/payment-history')}
                          style={{ cursor: 'pointer' }}
                        >
                          <div className="d-flex align-items-center">
                            <div className="bg-primary bg-opacity-10 rounded-3 p-2 me-3">
                              <i className="bi bi-receipt text-white fs-5"></i>
                            </div>
                            <div>
                              <p className="mb-0 fw-medium text-dark">Payment History</p>
                              <small className="text-muted">View booking payments and receipts</small>
                            </div>
                          </div>
                          <i className="bi bi-chevron-right text-muted"></i>
                        </button>

                        <button
                          type="button"
                          className="w-100 d-flex align-items-center justify-content-between py-3 border-bottom border-0 bg-transparent text-start"
                          onClick={() => navigate('/user/saved-cards')}
                          style={{ cursor: 'pointer' }}
                        >
                          <div className="d-flex align-items-center">
                            <div className="bg-primary bg-opacity-10 rounded-3 p-2 me-3">
                              <i className="bi bi-credit-card-2-front text-white fs-5"></i>
                            </div>
                            <div>
                              <p className="mb-0 fw-medium text-dark">Saved Cards</p>
                              <small className="text-muted">Add, update or remove payment cards</small>
                            </div>
                          </div>
                          <i className="bi bi-chevron-right text-muted"></i>
                        </button>
                      </div>

                      {/* Security */}
                      <div className="mb-4">
                        <h6 className="fw-bold text-dark mb-3">Security</h6>

                        <div className="d-flex align-items-center justify-content-between py-3 border-bottom">
                          <div className="d-flex align-items-center">
                            <div className="bg-light rounded-3 p-2 me-3">
                              <i className="bi bi-shield-lock text-muted fs-5"></i>
                            </div>
                            <div>
                              <p className="mb-0 fw-medium">Two-Factor Authentication</p>
                              <small className="text-muted">
                                After password login, verify with an OTP sent to your email.
                              </small>
                            </div>
                          </div>
                          <div className="form-check form-switch mb-0">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              role="switch"
                              id="twoFactorEnabled"
                              checked={settings.twoFactorEnabled}
                              onChange={() => handleToggle('twoFactorEnabled')}
                              style={{ width: '3rem', height: '1.5rem', cursor: 'pointer' }}
                            />
                          </div>
                        </div>
                      </div>

                      {/* Notification Access */}
                      <div className="mb-4">
                        <h6 className="fw-bold text-dark mb-3">Notification Access</h6>
                        
                        <div className="d-flex align-items-center justify-content-between py-3 border-bottom">
                          <div className="d-flex align-items-center">
                            <div className="bg-light rounded-3 p-2 me-3">
                              <i className="bi bi-bell text-muted fs-5"></i>
                            </div>
                            <div>
                              <p className="mb-0 fw-medium">Enable Notifications</p>
                              <small className="text-muted">Receive updates and supportive nudges.</small>
                            </div>
                          </div>
                          <div className="form-check form-switch mb-0">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              role="switch"
                              id="enableNotifications"
                              checked={settings.enableNotifications}
                              onChange={() => handleToggle('enableNotifications')}
                              style={{ width: '3rem', height: '1.5rem', cursor: 'pointer' }}
                            />
                          </div>
                        </div>
                      </div>

                      {/* Location Access */}
                      <div className="mb-4">
                        <h6 className="fw-bold text-dark mb-3">Location Access</h6>
                        
                        <div className="d-flex align-items-center justify-content-between py-3 border-bottom">
                          <div className="d-flex align-items-center">
                            <div className="bg-light rounded-3 p-2 me-3">
                              <i className="bi bi-geo-alt text-muted fs-5"></i>
                            </div>
                            <div>
                              <p className="mb-0 fw-medium">Allow Location</p>
                              <small className="text-muted">Use location to suggest nearby resources.</small>
                            </div>
                          </div>
                          <div className="form-check form-switch mb-0">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              role="switch"
                              id="allowLocation"
                              checked={settings.allowLocation}
                              onChange={() => handleToggle('allowLocation')}
                              style={{ width: '3rem', height: '1.5rem', cursor: 'pointer' }}
                            />
                          </div>
                        </div>

                        <div className="d-flex align-items-center justify-content-between py-3 border-bottom">
                          <div className="d-flex align-items-center">
                            <div className="bg-light rounded-3 p-2 me-3">
                              <i className="bi bi-cursor text-muted fs-5"></i>
                            </div>
                            <div>
                              <p className="mb-0 fw-medium">While Using App</p>
                              <small className="text-muted">Only access location during active sessions.</small>
                            </div>
                          </div>
                          <div className="form-check form-switch mb-0">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              role="switch"
                              id="whileUsingApp"
                              checked={settings.whileUsingApp}
                              onChange={() => handleToggle('whileUsingApp')}
                              style={{ width: '3rem', height: '1.5rem', cursor: 'pointer' }}
                            />
                          </div>
                        </div>
                      </div>

                      {/* Gallery Access */}
                      <div className="mb-4">
                        <h6 className="fw-bold text-dark mb-3">Gallery Access</h6>
                        
                        <div className="d-flex align-items-center justify-content-between py-3 border-bottom">
                          <div className="d-flex align-items-center">
                            <div className="bg-light rounded-3 p-2 me-3">
                              <i className="bi bi-images text-muted fs-5"></i>
                            </div>
                            <div>
                              <p className="mb-0 fw-medium">Allow Gallery Access</p>
                              <small className="text-muted">Attach images to share mood boards or notes.</small>
                            </div>
                          </div>
                          <div className="form-check form-switch mb-0">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              role="switch"
                              id="allowGalleryAccess"
                              checked={settings.allowGalleryAccess}
                              onChange={() => handleToggle('allowGalleryAccess')}
                              style={{ width: '3rem', height: '1.5rem', cursor: 'pointer' }}
                            />
                          </div>
                        </div>

                        <div className="d-flex align-items-center justify-content-between py-3">
                          <div className="d-flex align-items-center">
                            <div className="bg-light rounded-3 p-2 me-3">
                              <i className="bi bi-question-circle text-muted fs-5"></i>
                            </div>
                            <div>
                              <p className="mb-0 fw-medium">Ask Every Time</p>
                              <small className="text-muted">Request permission before each upload.</small>
                            </div>
                          </div>
                          <div className="form-check form-switch mb-0">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              role="switch"
                              id="askEveryTime"
                              checked={settings.askEveryTime}
                              onChange={() => handleToggle('askEveryTime')}
                              style={{ width: '3rem', height: '1.5rem', cursor: 'pointer' }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default UserSettings;
